import logger from "@logger";
import photoUploadService from "@modules/photoUpload/services/photoUploadService";
import { useCurrentUser } from "@shared/queries";
import QUERY_KEYS from "@shared/queryKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * React Query mutation hook for uploading profile photos.
 *
 * Provides photo upload functionality with proper error handling
 * and cache invalidation during the onboarding flow.
 *
 * Features:
 * - Uploads cropped and compressed profile photos to Firebase Storage
 * - Generates unique filenames with timestamp prefix
 * - Invalidates user queries on success to refetch updated profile
 * - Logs errors for debugging
 * - Requires authenticated user
 *
 * @example
 * ```tsx
 * const { mutate, isPending, error, data } = useUploadProfilePhoto();
 *
 * const handleUpload = (file: File) => {
 *   mutate(file, {
 *     onSuccess: (photoUrl) => {
 *       console.log('Photo uploaded:', photoUrl);
 *     }
 *   });
 * };
 * ```
 */
export function useUploadProfilePhoto() {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: (file: File) => {
      if (!user) {
        throw new Error("User not authenticated");
      }
      return photoUploadService.uploadProfilePhoto(file, user.uid);
    },

    onSuccess: async () => {
      if (!user) {
        return;
      }

      // Invalidate and refetch user data to get the new photo URL
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] });
    },

    onError: (error) => {
      logger.error("Failed to upload profile photo:", error);
    },
  });
}

export default useUploadProfilePhoto;
